import { Box, Flex, Icon, Input } from '@cowsquare/design';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { getQueryObject } from '../../../../utils/getQueryObject';
import { getTagClause, getTitleClause } from '../../../../utils/getWhereClause';
import { useAdsQuery, useFilterTags, usePagesBindingUrl, useSearchText } from '../../../../utils/hooks';
import { Advertisements } from '../../../Advertisements';
import { ArticleCardList } from '../../../ArticleCardList';
import { FilterTags } from '../../../FilterTags';
import { ArticleCardListLoading, TagListLoading } from '../../../Loading';
import { NoResult } from '../../../NoResult';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import { getHTMLString } from '../../../../utils/getHTMLString';

const PAGE_SIZE = 20;
// TODO: 換成 e drug 的 sales query
const GET_E_DRUG_SALES = gql`
  query getEDrugSales($limit: Int, $offset: Int, $where: hkdutest_e_drug_sales_bool_exp) {
    tags: hkdutest_e_drug_sales_tags {
      name
    }

    total: hkdutest_e_drug_sales_aggregate(where: $where) {
      aggregate {
        count
      }
    }

    list: hkdutest_e_drug_sales(
      limit: $limit
      order_by: { priority: asc, created_at: desc }
      offset: $offset
      where: $where
    ) {
      id
      author {
        real_avatar
        name
        email
      }
      name
      e_drug_content {
        content
      }
      cover_file {
        link
      }
      tags {
        tag {
          name
        }
      }
      created_at
    }
  }
`;

const SalesList = ({ location, uri }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);
  const [tag, handleTagChange] = useFilterTags(queryObject, uri);
  const [searchText, handleSearch] = useSearchText(queryObject, uri);
  const { ads } = useAdsQuery(uri);
  const tagFilter = getTagClause(tag);
  const titleFilter = getTitleClause(searchText);

  return (
    <Query
      query={GET_E_DRUG_SALES}
      variables={R.reject(R.isNil, {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        where: { ...tagFilter, ...titleFilter, expiry_date: { _gt: 'now' } },
      })}
    >
      {({ data, loading }) => {
        const list = R.propOr([], 'list', data);
        const tags = R.pipe(
          R.propOr([], 'tags'),
          R.pluck('name'),
          R.unless(R.isEmpty, R.prepend('All'))
        )(data);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);

        return (
          <Section>
            <Helmet>
              <title>Drug News - HKDU E-Pharm</title>
            </Helmet>
            <Advertisements ads={ads} />
            <Box px={[5, 0]} pt={8}>
              <Box mb={5}>
                <Flex flexDirection={['column', 'row']} justifyContent="space-between">
                  <Title m={0} mb={[5, 0]}>
                    Drug News
                  </Title>
                  <Box width={[1, '200px']} mt={[6, 0]}>
                    <Input
                      placeholder="Search"
                      suffix={<Icon name="SEARCH" />}
                      defaultValue={searchText}
                      onChange={handleSearch}
                    />
                  </Box>
                </Flex>
              </Box>
              <Box mb={6}>
                {R.and(R.isEmpty(tags), loading) && <TagListLoading />}
                <FilterTags value={tag} tags={tags} onChange={handleTagChange} />
              </Box>
              <Box pb={8}>
                {R.and(R.isEmpty(list), !loading) && <NoResult>No result found...</NoResult>}
                {R.and(R.isEmpty(list), loading) && <ArticleCardListLoading />}
                <ArticleCardList
                  uri={uri}
                  page={page}
                  onPageChange={onPageChange}
                  list={list}
                  total={total}
                  options={item => ({
                    description: R.pipe(R.pathOr('', ['e_drug_content', 'content']), getHTMLString)(item),
                  })}
                />
              </Box>
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default SalesList;
